import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaTimes, FaCheckCircle, FaUser, FaBuilding, FaMapMarkerAlt, FaEnvelope, FaArrowLeft, FaCalendarAlt, FaChevronDown, FaChevronUp } from "react-icons/fa";
import blockClubLogo from "../../media/blockclub-logo.png";
import { appendCompanyCode } from "../../hooks/useCompanyCode";
import "./VenueInterest.css";

// US States list
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

export default function VenueInterest() {
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
  const navigate = useNavigate();

  // OTP state
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    contact_name: "",
    phone: "",
    business_name: "",
    role: "owner",
    street_address: "",
    street_address_2: "",
    city: "",
    state: "MO",
    postal_code: "",
    interested_in_hosting: false,
    space_capacity: "",
    food_options: [],
    drink_options: [],
    preferred_times: [],
    hosting_notes: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // OTP Functions
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setEmailError("");
    setOtpError("");

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setSendingOtp(true);

    try {
      const response = await fetch(`${apiUrl}/loyalty/email-otp/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to send OTP");
      }

      setOtpSent(true);
      setResendCooldown(60);
    } catch (error) {
      console.error("Error sending OTP:", error);
      setEmailError(error.message || "Failed to send verification code. Please try again.");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setOtpError("");

    if (!otpCode || otpCode.length !== 6) {
      setOtpError("Please enter the complete 6-digit verification code");
      return;
    }

    setVerifyingOtp(true);

    try {
      const response = await fetch(`${apiUrl}/loyalty/email-otp/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: otpCode }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Invalid verification code");
      }

      // Email verified successfully, show the form
      setShowForm(true);
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setOtpError(error.message || "Invalid verification code. Please try again.");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0 || sendingOtp) return;

    setOtpError("");
    setSendingOtp(true);

    try {
      const response = await fetch(`${apiUrl}/loyalty/email-otp/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to resend OTP");
      }

      setResendCooldown(60);
    } catch (error) {
      console.error("Error resending OTP:", error);
      setOtpError(error.message || "Failed to resend code. Please try again.");
    } finally {
      setSendingOtp(false);
    }
  };

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-numeric characters
    const cleaned = value.replace(/\D/g, '');

    // Format as (XXX) XXX-XXXX
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 3) return `(${cleaned}`;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  // Form Functions
  const handleFormChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else if (name === 'postal_code') {
      const cleaned = value.replace(/\D/g, '').slice(0, 5);
      setFormData(prev => ({ ...prev, [name]: cleaned }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleCheckboxGroup = (field, value) => {
    setFormData(prev => {
      const current = prev[field];
      return {
        ...prev,
        [field]: current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value],
      };
    });
  };

  const handleHostingToggle = () => {
    setFormData(prev => ({ ...prev, interested_in_hosting: !prev.interested_in_hosting }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.contact_name.trim()) {
      errors.contact_name = "Contact name is required";
    }

    if (!formData.business_name.trim()) {
      errors.business_name = "Business name is required";
    }

    if (!formData.street_address.trim()) {
      errors.street_address = "Street address is required";
    }

    if (!formData.city.trim()) {
      errors.city = "City is required";
    }

    if (!formData.state.trim()) {
      errors.state = "State is required";
    }

    if (!formData.postal_code.trim()) {
      errors.postal_code = "ZIP code is required";
    } else if (formData.postal_code.length !== 5) {
      errors.postal_code = "ZIP code must be 5 digits";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      // Combine address lines
      const combinedAddress = formData.street_address_2
        ? `${formData.street_address}, ${formData.street_address_2}`
        : formData.street_address;

      const response = await fetch(`${apiUrl}/loyalty/venue-leads/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contact_name: formData.contact_name,
          email: email,
          phone: formData.phone,
          business_name: formData.business_name,
          role: formData.role,
          street_address: combinedAddress,
          city: formData.city,
          state: formData.state,
          postal_code: formData.postal_code,
          interested_in_hosting: formData.interested_in_hosting,
          space_capacity: formData.interested_in_hosting && formData.space_capacity
            ? parseInt(formData.space_capacity, 10)
            : null,
          food_options: formData.interested_in_hosting && formData.food_options.length > 0
            ? formData.food_options
            : null,
          drink_options: formData.interested_in_hosting && formData.drink_options.length > 0
            ? formData.drink_options
            : null,
          preferred_times: formData.interested_in_hosting && formData.preferred_times.length > 0
            ? formData.preferred_times
            : null,
          hosting_notes: formData.interested_in_hosting && formData.hosting_notes.trim()
            ? formData.hosting_notes.trim()
            : null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to submit partnership interest");
      }

      setSubmitSuccess(true);

      // Redirect to home after 3 seconds
      setTimeout(() => {
        navigate(appendCompanyCode("/perks-directory"));
      }, 3000);

    } catch (error) {
      console.error("Error submitting venue interest:", error);
      setFormErrors({ submit: error.message || "Failed to submit. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  // Success View
  if (submitSuccess) {
    return (
      <div className="venue-interest-page">
        <div className="venue-interest-container success-container">
          <div className="venue-interest-header">
            <img src={blockClubLogo} alt="Block Club" className="venue-interest-logo" />
          </div>

          <div className="venue-interest-success-content">
            <FaCheckCircle className="success-icon-large" />
            <h1>Thank You!</h1>
            <p>We've received your partnership interest. Our team will reach out to you shortly!</p>
            <p className="redirect-notice">Redirecting you to home...</p>
          </div>
        </div>
      </div>
    );
  }

  // Form View (after OTP verification)
  if (showForm) {
    return (
      <div className="venue-interest-page">
        <div className="venue-interest-container">
          <div className="venue-interest-header">
            <button
              onClick={() => navigate(appendCompanyCode("/perks-directory"))}
              className="venue-interest-back-btn"
              aria-label="Go back to home"
            >
              <FaArrowLeft />
            </button>
            <img src={blockClubLogo} alt="Block Club" className="venue-interest-logo" />
          </div>

          <div className="venue-interest-content">
            <div className="venue-interest-title">
              <h1>Tell us about your venue</h1>
            </div>

            <form onSubmit={handleFormSubmit} className="venue-interest-form">
              {/* Contact Information */}
              <div className="form-section">
                <h3 className="form-section-title">
                  <FaUser /> Contact Information
                </h3>

                <div className="standard-form-group">
                  <label className="standard-label">Full Name *</label>
                  <input
                    type="text"
                    name="contact_name"
                    className="standard-input"
                    placeholder="John Smith"
                    value={formData.contact_name}
                    onChange={handleFormChange}
                    autoFocus
                  />
                  {formErrors.contact_name && (
                    <div className="form-error">
                      <FaTimes /> {formErrors.contact_name}
                    </div>
                  )}
                </div>

                <div className="standard-form-group">
                  <label className="standard-label">Email</label>
                  <input
                    type="email"
                    className="standard-input verified-input"
                    value={email}
                    disabled
                  />
                  <small className="field-hint">Email verified</small>
                </div>

                <div className="standard-form-group">
                  <label className="standard-label">Phone (Optional)</label>
                  <input
                    type="tel"
                    name="phone"
                    className="standard-input"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={handleFormChange}
                    maxLength={14}
                  />
                </div>
              </div>

              {/* Business Information */}
              <div className="form-section">
                <h3 className="form-section-title">
                  <FaBuilding /> Business Information
                </h3>

                <div className="standard-form-group">
                  <label className="standard-label">Business Name *</label>
                  <input
                    type="text"
                    name="business_name"
                    className="standard-input"
                    placeholder="Joe's Coffee Shop"
                    value={formData.business_name}
                    onChange={handleFormChange}
                  />
                  {formErrors.business_name && (
                    <div className="form-error">
                      <FaTimes /> {formErrors.business_name}
                    </div>
                  )}
                </div>

                <div className="standard-form-group">
                  <label className="standard-label">Your Role *</label>
                  <select
                    name="role"
                    className="standard-input"
                    value={formData.role}
                    onChange={handleFormChange}
                  >
                    <option value="owner">Owner</option>
                    <option value="manager">Manager</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {/* Location */}
              <div className="form-section">
                <h3 className="form-section-title">
                  <FaMapMarkerAlt /> Location
                </h3>

                <div className="standard-form-group">
                  <label className="standard-label">Address Line 1 *</label>
                  <input
                    type="text"
                    name="street_address"
                    className="standard-input"
                    placeholder="123 Main Street"
                    value={formData.street_address}
                    onChange={handleFormChange}
                  />
                  {formErrors.street_address && (
                    <div className="form-error">
                      <FaTimes /> {formErrors.street_address}
                    </div>
                  )}
                </div>

                <div className="standard-form-group">
                  <label className="standard-label">Address Line 2 (Optional)</label>
                  <input
                    type="text"
                    name="street_address_2"
                    className="standard-input"
                    placeholder="Apt, Suite, Unit, etc."
                    value={formData.street_address_2}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="form-row">
                  <div className="standard-form-group">
                    <label className="standard-label">City *</label>
                    <input
                      type="text"
                      name="city"
                      className="standard-input"
                      placeholder="New York"
                      value={formData.city}
                      onChange={handleFormChange}
                    />
                    {formErrors.city && (
                      <div className="form-error">
                        <FaTimes /> {formErrors.city}
                      </div>
                    )}
                  </div>

                  <div className="standard-form-group form-group-small">
                    <label className="standard-label">State *</label>
                    <select
                      name="state"
                      className="standard-input"
                      value={formData.state}
                      onChange={handleFormChange}
                    >
                      {US_STATES.map(state => (
                        <option key={state.code} value={state.code}>
                          {state.code}
                        </option>
                      ))}
                    </select>
                    {formErrors.state && (
                      <div className="form-error">
                        <FaTimes /> {formErrors.state}
                      </div>
                    )}
                  </div>

                  <div className="standard-form-group form-group-small">
                    <label className="standard-label">ZIP Code *</label>
                    <input
                      type="text"
                      name="postal_code"
                      className="standard-input"
                      placeholder="12345"
                      value={formData.postal_code}
                      onChange={handleFormChange}
                      maxLength={5}
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                    {formErrors.postal_code && (
                      <div className="form-error">
                        <FaTimes /> {formErrors.postal_code}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Hosting Interest Section */}
              <div className="form-section hosting-section">
                <button
                  type="button"
                  className={`hosting-toggle ${formData.interested_in_hosting ? "hosting-toggle--active" : ""}`}
                  onClick={handleHostingToggle}
                  aria-expanded={formData.interested_in_hosting}
                >
                  <div className="hosting-toggle-left">
                    <div className={`hosting-toggle-icon ${formData.interested_in_hosting ? "hosting-toggle-icon--active" : ""}`}>
                      <FaCalendarAlt />
                    </div>
                    <div>
                      <span className="hosting-toggle-title">Interested in hosting events?</span>
                      <span className="hosting-toggle-subtitle">Let us know about your space</span>
                    </div>
                  </div>
                  {formData.interested_in_hosting ? <FaChevronUp className="hosting-chevron" /> : <FaChevronDown className="hosting-chevron" />}
                </button>

                {formData.interested_in_hosting && (
                  <div className="hosting-fields">
                    {/* Space Capacity */}
                    <div className="standard-form-group">
                      <label className="standard-label">Space Capacity (max guests)</label>
                      <input
                        type="number"
                        name="space_capacity"
                        className="standard-input"
                        placeholder="e.g. 50"
                        value={formData.space_capacity}
                        onChange={handleFormChange}
                        min={1}
                        max={10000}
                        inputMode="numeric"
                      />
                    </div>

                    {/* Food Options */}
                    <div className="standard-form-group">
                      <label className="standard-label">Food Options</label>
                      <div className="checkbox-grid">
                        {[
                          { value: "full_kitchen", label: "Full kitchen / hot food" },
                          { value: "light_bites", label: "Light bites & snacks" },
                          { value: "outside_catering", label: "Outside catering welcome" },
                          { value: "no_food", label: "No food service" },
                        ].map(opt => (
                          <label key={opt.value} className="checkbox-option">
                            <input
                              type="checkbox"
                              checked={formData.food_options.includes(opt.value)}
                              onChange={() => handleCheckboxGroup("food_options", opt.value)}
                            />
                            <span>{opt.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Drink Options */}
                    <div className="standard-form-group">
                      <label className="standard-label">Drink Options</label>
                      <div className="checkbox-grid">
                        {[
                          { value: "full_bar", label: "Full bar" },
                          { value: "beer_and_wine", label: "Beer & wine only" },
                          { value: "non_alcoholic", label: "Non-alcoholic only" },
                          { value: "byob", label: "BYOB welcome" },
                        ].map(opt => (
                          <label key={opt.value} className="checkbox-option">
                            <input
                              type="checkbox"
                              checked={formData.drink_options.includes(opt.value)}
                              onChange={() => handleCheckboxGroup("drink_options", opt.value)}
                            />
                            <span>{opt.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Preferred Times */}
                    <div className="standard-form-group">
                      <label className="standard-label">Preferred Event Times</label>
                      <div className="checkbox-grid">
                        {[
                          { value: "weekday_morning", label: "Weekday mornings (before noon)" },
                          { value: "weekday_afternoon", label: "Weekday afternoons (noon–5pm)" },
                          { value: "weekday_evening", label: "Weekday evenings (5pm+)" },
                          { value: "weekend_daytime", label: "Weekend daytime (before 5pm)" },
                          { value: "weekend_evening", label: "Weekend evenings (5pm+)" },
                        ].map(opt => (
                          <label key={opt.value} className="checkbox-option">
                            <input
                              type="checkbox"
                              checked={formData.preferred_times.includes(opt.value)}
                              onChange={() => handleCheckboxGroup("preferred_times", opt.value)}
                            />
                            <span>{opt.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Additional Notes */}
                    <div className="standard-form-group">
                      <label className="standard-label">Additional Notes (Optional)</label>
                      <textarea
                        name="hosting_notes"
                        className="standard-input hosting-textarea"
                        placeholder="Anything else we should know about your space or hosting preferences..."
                        value={formData.hosting_notes}
                        onChange={handleFormChange}
                        rows={3}
                        maxLength={1000}
                      />
                      <small className="field-hint">{formData.hosting_notes.length}/1000 characters</small>
                    </div>
                  </div>
                )}
              </div>

              {formErrors.submit && (
                <div className="form-error submit-error">
                  <FaTimes /> {formErrors.submit}
                </div>
              )}

              <button
                type="submit"
                className="btn-primary submit-btn"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="btn-spinner"></span>
                    Submitting...
                  </>
                ) : (
                  <>
                    <FaCheckCircle />
                    Submit Partnership Interest
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // OTP View (initial state)
  return (
    <div className="venue-interest-page">
      <div className="venue-interest-container">
        <div className="venue-interest-header">
          <button
            onClick={() => navigate(appendCompanyCode("/perks-directory"))}
            className="venue-interest-back-btn"
            aria-label="Go back to home"
          >
            <FaArrowLeft />
          </button>
          <img src={blockClubLogo} alt="Block Club" className="venue-interest-logo" />
        </div>

        <div className="venue-interest-content">
          <div className="venue-interest-title">
            <h1>
              {otpSent
                ? "Enter verification code"
                : "Verify your email"}
            </h1>
          </div>

          {!otpSent ? (
            <form onSubmit={handleSendOTP} className="venue-interest-form">
              <div className="standard-form-group">
                <label className="standard-label">Email Address</label>
                <input
                  type="email"
                  className="standard-input"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError("");
                  }}
                  autoFocus
                />
                {emailError && (
                  <div className="form-error">
                    <FaTimes /> {emailError}
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="btn-primary submit-btn"
                disabled={sendingOtp}
              >
                {sendingOtp ? (
                  <>
                    <span className="btn-spinner"></span>
                    Sending Code...
                  </>
                ) : (
                  <>
                    <FaEnvelope />
                    Send Verification Code
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="venue-interest-form">
              <div className="standard-form-group">
                <label className="standard-label">Verification Code</label>
                <input
                  type="text"
                  className="standard-input"
                  placeholder="Enter 6-digit code"
                  value={otpCode}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "");
                    setOtpCode(v);
                    setOtpError("");
                  }}
                  autoFocus
                  maxLength={6}
                  disabled={verifyingOtp}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                {otpError && (
                  <div className="form-error">
                    <FaTimes /> {otpError}
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="btn-primary submit-btn"
                disabled={verifyingOtp || otpCode.length !== 6}
              >
                {verifyingOtp ? (
                  <>
                    <span className="btn-spinner"></span>
                    Verifying...
                  </>
                ) : (
                  <>
                    <FaCheckCircle />
                    Verify Code
                  </>
                )}
              </button>

              <div className="resend-section">
                {resendCooldown > 0 ? (
                  <p className="resend-timer">
                    Resend code in {resendCooldown}s
                  </p>
                ) : (
                  <p className="resend-link-container">
                    {sendingOtp ? (
                      <span className="resend-text-disabled">
                        <span className="btn-spinner-small"></span>
                        Sending code...
                      </span>
                    ) : (
                      <>
                        Didn't receive a code?{' '}
                        <button
                          type="button"
                          onClick={handleResendOTP}
                          className="resend-link"
                          disabled={sendingOtp}
                        >
                          Resend
                        </button>
                      </>
                    )}
                  </p>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
